import React, {useEffect, useState} from "react";
import { keyframes } from '@mui/styled-engine';
import {useSelector} from "react-redux";

import {useTranslation} from "react-i18next";

import useDataSource from "../../../hooks/useDataSource";
import * as DataUtilities from "../../../utilities/DataUtilities";

import { styled, Typography } from '@mui/material';

const PREFIX = 'SmartTicker';

const classes = {
    ticker: `${PREFIX}-ticker`,
    title: `${PREFIX}-title`,
    content: `${PREFIX}-content`
};

const scrollLeft = keyframes`
	from {
		transform: translate3d(0, 0, 0);
		visibility: visible;
	}
  
	to {
		transform: translate3d(-100%, 0, 0);
	}
`;

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.ticker}`]: {
		overflow: "hidden",
		paddingLeft: "100%",
		boxSizing: "content-box"
	},

    [`& .${classes.title}`]: {
		borderRight: "2px solid gray",
		marginLeft: "-10px",
		padding: "0 16px",
		position: "absolute",
		left: 0,
		marginRight: "16px",
		color: "#dd951a",
		zIndex: "10",
		background: "#676767",
	},

    [`& .${classes.content}`]: {
		cursor: "pointer",
		fontWeight: "300",
		display: "inline-block",
		height: "100%",
		whiteSpace: "nowrap",
		paddingRight: "100%",
		boxSizing: "content-box",
		color: "white",
		animation: `${scrollLeft} 60s linear infinite`,
		"&:hover": {
			animationPlayState: "paused",
		}
	}
}));

export default function SmartTicker({configuration}) {
	const {t} = useTranslation();

	const dataSourceName = configuration.dataSource?.name !== undefined ? configuration.dataSource?.name : "";
	const field = configuration.dataSource?.fields?.valueField !== undefined ? configuration.dataSource?.fields?.valueField : "";
	const limitDataSet = configuration.dataSource?.fields?.limitDataSet !== undefined ? configuration.dataSource?.fields?.limitDataSet : null;
	const limitDataSetDefaultVal = configuration.dataSource?.fields?.limitDataSetDefaultVal !== undefined ? configuration.dataSource?.fields?.limitDataSetDefaultVal : null;
	const condition = configuration.dataSource?.condition !== undefined ? configuration.dataSource?.condition : null;
	const title = configuration?.name?.title;

	// App State
	const dataSource = useDataSource(dataSourceName);
	const persist = useSelector((state) => state.persist);

	// Component State
	const [content, setContent] = useState(null);

	// Data
	useEffect(() => {
		if (dataSource.data && dataSource.fetched) {
			let uniqueContents = [];

			if (dataSource.data.length > 1) {
				switch (limitDataSet) {
					case "zero":
						uniqueContents.push(limitDataSetDefaultVal);
						break;
					case "one":
						if (condition && dataSource.data[0][field] !== null) {
							const val = DataUtilities.filterDataSet(condition.conditionType,condition.conditionOperator,condition.conditionField,condition.conditionValue,dataSource.data[0],field);
							if (val) {
								// Don't push NULL data
								uniqueContents.push(val);
							}
						} else {
							if (dataSource.data[0][field] !== null) {
								uniqueContents.push(dataSource.data[0][field]);
							}
						}
						break;
					case "many":
					default:
						for (let i = 0; i < dataSource.data.length; i++) {
							if (condition && dataSource.data[i][field] !== null) {
								const val = DataUtilities.filterDataSet(condition.conditionType,condition.conditionOperator,condition.conditionField,condition.conditionValue,dataSource.data[i],field);
								if (val) {
									// Don't push NULL data
									uniqueContents.push(val);
								}
							} else {
								if (dataSource.data[i][field] !== null) {
									uniqueContents.push(dataSource.data[i][field]);
								}
							}
						}
						break;
				}
			} else if (dataSource.data.length === 1) {
				if (condition && dataSource.data[0][field] !== null) {
					const val = DataUtilities.filterDataSet(condition.conditionType,condition.conditionOperator,condition.conditionField,condition.conditionValue,dataSource.data[0],field);
					if (val) {
						// Don't push NULL data
						uniqueContents.push(val);
					}
				} else {
					if (dataSource.data[0][field] !== null) {
						uniqueContents.push(dataSource.data[0][field]);
					}
				}
			}

			const formattedWriteups = uniqueContents.map((item, idx) =>
				setLayout(item, idx)
			);

			setContent(formattedWriteups);
		} else {
			setContent("");
		}
	}, [dataSource.data, persist.themeLight]);

	const setLayout = (content, idx) => {
		return (<span key={idx + 1} className={classes.content} dangerouslySetInnerHTML={{__html: content}} />);
	};

	return (
        <Root className={classes.ticker}>
			<Typography variant="h6">
				<span className={classes.title}>{`${t(title)}`}</span>
				{content}
			</Typography>
		</Root>
    );
}
